import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { clientService } from '../../api/services';
import { Client, CATEGORIE_CLIENT_LABELS, Page } from '../../types';
import { formatMatriculeFiscal } from '../../utils/formatters';
import { DataTable, Column } from '../../components/common/DataTable';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import toast from 'react-hot-toast';

export function ClientList() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categorieFilter, setCategorieFilter] = useState<string>('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page: currentPage, size: 10 };
      if (search) params.search = search;
      if (categorieFilter) params.categorie = categorieFilter;
      const data: Page<Client> = await clientService.getAll(params);
      setClients(data.content);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);
    } catch (error) {
      toast.error(t.messages.loadError);
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, categorieFilter, t.messages.loadError]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await clientService.delete(deleteId);
      toast.success(t.messages.deleteSuccess);
      loadClients();
    } catch (error) {
      toast.error(t.messages.deleteError);
    } finally {
      setDeleteId(null);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(0);
    loadClients();
  };

  const columns: Column<Client>[] = [
    {
      key: 'code',
      header: t.common.code,
      sortable: true,
      render: (client) => (
        <span className="font-medium text-primary-600">{client.code}</span>
      ),
    },
    {
      key: 'raisonSociale',
      header: t.clients.raisonSociale,
      sortable: true,
      render: (client) => (
        <div>
          <div className="font-medium text-gray-900">{client.raisonSociale}</div>
          {client.ville && (
            <div className="text-xs text-gray-500">{client.ville}</div>
          )}
        </div>
      ),
    },
    {
      key: 'matriculeFiscal',
      header: t.clients.matriculeFiscal,
      render: (client) => (
        <span className="text-sm text-gray-600 font-mono">
          {formatMatriculeFiscal(client.matriculeFiscal)}
        </span>
      ),
    },
    {
      key: 'categorie',
      header: t.clients.categorie,
      render: (client) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
          {CATEGORIE_CLIENT_LABELS[client.categorie]}
        </span>
      ),
    },
    {
      key: 'telephone',
      header: t.common.telephone,
      render: (client) => (
        <span className="text-sm text-gray-600">{client.telephone || client.mobile || '-'}</span>
      ),
    },
    {
      key: 'actif',
      header: t.common.status,
      render: (client) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          client.actif ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {client.actif ? t.common.active : t.common.inactive}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t.common.actions,
      render: (client) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/clients/${client.id}`);
            }}
            className="text-primary-600 hover:text-primary-800 p-1"
            title={t.common.edit}
          >
            <PencilSquareIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteId(client.id);
            }}
            className="text-red-600 hover:text-red-800 p-1"
            title={t.common.delete}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">{t.clients.title}</h1>
          <p className="page-subtitle">{totalElements} {t.clients.title.toLowerCase()}</p>
        </div>
        <button
          onClick={() => navigate('/clients/new')}
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-1" />
          {t.clients.newClient}
        </button>
      </div>

      <div className="card">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={`${t.common.search}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10"
            />
          </div>
          <select
            value={categorieFilter}
            onChange={(e) => {
              setCategorieFilter(e.target.value);
              setCurrentPage(0);
            }}
            className="input-field sm:w-48"
          >
            <option value="">{t.common.all} - {t.clients.categorie}</option>
            {Object.entries(CATEGORIE_CLIENT_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <button type="submit" className="btn-primary">
            {t.common.search}
          </button>
        </form>
      </div>

      <DataTable
        columns={columns}
        data={clients}
        totalElements={totalElements}
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        loading={loading}
        emptyMessage={t.common.noResults}
        onRowClick={(client) => navigate(`/clients/${client.id}`)}
      />

      <ConfirmDialog
        isOpen={deleteId !== null}
        title={t.messages.confirmDelete}
        message={t.clients.deleteConfirm}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}

export default ClientList;
